import { meQueryOptions, useUserId } from "@/api/auth";
import { infinitePurchaseHistoryQueryOptions, prefetchInfinitePurchaseHistory } from "@/api/purchases";
import { subscriptionsQuery } from "@/api/subscriptions";
import { InfiniteScrollEnd } from "@/components/InfiniteScrollEnd";
import { PurchaseDialog } from "@/components/PurchaseDialog";
import { ButtonLink } from "@/components/ui/Link";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { seo } from "@/utils/seo";
import { Box, Center, Flex, Heading, QueryGate, Spinner, Text } from "@givenwell/components";
import { ShoppingBagIcon } from "@givenwell/core-solid-icons";
import { PurchaseDetails } from "@givenwell/marketplace-api";
import { styled } from "@givenwell/stitches";
import { colors } from "@givenwell/theme";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useLayoutEffect, useRef, useState } from "react";
import { fallback, object, optional, string } from "valibot";
import { PurchaseCard } from "./PurchaseCard";
import { SubscriptionCard } from "./SubscriptionCard";

export const Route = createFileRoute("/_auth/_app/purchases/")({
  validateSearch: object({
    showReceipt: fallback(optional(string()), undefined),
  }),
  loader: () => {
    prefetchInfinitePurchaseHistory();
  },
  component: HistoryController,
  head: () => ({ meta: seo({ title: "Purchase History" }) }),
});

const route = getRouteApi("/_auth/_app/purchases/");

const isUpcoming = (purchase: PurchaseDetails) =>
  purchase.purchase_status === "upcoming" && purchase.starts_at && new Date(purchase.starts_at) > new Date();

const sections = ["upcoming", "active", "confirmed"] as const;
type Section = (typeof sections)[number];

function HistoryController() {
  const userId = useUserId();
  const purchaseHistoryQuery = useInfiniteQuery(infinitePurchaseHistoryQueryOptions(userId));
  const activeSubscriptionsQuery = useQuery(subscriptionsQuery);
  const { showReceipt } = route.useSearch();
  const navigate = useNavigate();
  const purchases = purchaseHistoryQuery.data?.pages.flat() || [];

  const upcomingPurchases = purchases.filter(isUpcoming);
  const activeSubscriptions = activeSubscriptionsQuery.data || [];
  const completedPurchases = purchases.filter(purchase => !isUpcoming(purchase));

  const hasUpcomingPurchases = upcomingPurchases.length > 0;
  const hasActiveSubscriptions = activeSubscriptions.length > 0;
  const hasCompletedPurchases = completedPurchases.length > 0;

  let nonEmptySectionCount = 0;
  if (hasUpcomingPurchases) nonEmptySectionCount++;
  if (hasActiveSubscriptions) nonEmptySectionCount++;
  if (hasCompletedPurchases) nonEmptySectionCount++;

  const showHeadings = nonEmptySectionCount > 1;
  const me = useQuery(meQueryOptions);

  const [active, setActive] = useState<Section>("upcoming");

  const scrollContainerRef = useRef<HTMLDivElement>(null!);

  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const scrollY = scrollContainer.scrollTop;

      for (const section of sections) {
        const sectionElement = document.getElementById(section);
        if (!sectionElement) {
          continue;
        }

        if (scrollY > 0 && scrollY >= scrollContainer.scrollHeight - scrollContainer.clientHeight) {
          const lastNonEmptySection = sections.findLast(section => !!document.getElementById(section));
          if (lastNonEmptySection) {
            setActive(lastNonEmptySection);
          }
          break;
        } else if (scrollY + scrollContainer.offsetTop + 50 > sectionElement.offsetTop) {
          setActive(section);
        }
      }
    };
    scrollContainer.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const empty = purchaseHistoryQuery.isSuccess && !purchaseHistoryQuery.data?.pages.flat().length;

  if (empty) {
    return (
      <PageWrapper>
        <Flex css={{ flexDirection: "column", flex: "1 0 0px", overflow: "hidden" }}>
          <Flex
            css={{
              items: "center",
              height: 64,
              justify: "center",
              align: "center",
              backgroundColor: colors.cream,
              borderBottom: `1px solid rgba(0, 0, 0, 0.05)`,

              width: "100%",
              maxW: 960,
              mx: "auto",

              display: "flex",
              "@lg": {
                borderBottom: "none",
              },
            }}
          >
            <Text css={{ fontScale: "lg", weight: 500 }}>Purchase History</Text>
          </Flex>
          <Flex
            css={{
              flex: "1 0 0px",
              justify: "center",
              items: "center",
              flexDir: "column",
              align: "center",
            }}
          >
            <Flex
              css={{
                flexDirection: "column",
                width: "100%",
                maxW: 540,
                gap: 40,
                pl: "max(16px, var(--safe-area-inset-left))",
                pr: "max(16px, var(--safe-area-inset-right))",
                "@md": {
                  maxW: 500,
                },
              }}
            >
              <Box css={{ spaceY: 20 }}>
                <Center
                  css={{
                    backgroundColor: colors.warmGrey,
                    width: "100%",
                    w: 72,
                    h: 72,
                    mx: "auto",
                    borderRadius: "50%",
                  }}
                >
                  <ShoppingBagIcon width="28" height="28" />
                </Center>
                <Box css={{ px: 20, width: "100%", maxW: 284, mx: "auto" }}>
                  <Text css={{ fontScale: "md", weight: 500 }}>You have not purchased anything</Text>
                </Box>
              </Box>
              <Flex
                css={{
                  flexDir: "column",
                  gap: 16,
                  px: 16,
                  "@md": { display: "grid", gridCols: 2 },
                }}
              >
                <ButtonLink variant="filled" size="lg" color="primary" to="/marketplace">
                  Go Shopping
                </ButtonLink>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Flex css={{ flexDirection: "column", flex: "1 0 0px", overflow: "hidden" }}>
        <Box
          css={{
            minHeight: 64,
            display: "flex",
            items: "center",
            align: "center",
            justify: "center",
            fontScale: "lg",
            weight: 500,

            borderBottom: `1px solid rgba(0, 0, 0, 0.05)`,

            width: "100%",
            maxW: 960,
            mx: "auto",

            "@lg": {
              borderBottom: "none",
              justify: "flex-start",
            },
          }}
        >
          Purchase History
        </Box>
        <QueryGate
          queries={[activeSubscriptionsQuery, purchaseHistoryQuery]}
          success={() => (
            <>
              {showHeadings && (
                <MobileNav>
                  {hasUpcomingPurchases && (
                    <MobileNavLink href="#upcoming" data-active={active === "upcoming" ? "" : undefined}>
                      Upcoming Bookings & Events <CountBadge>{upcomingPurchases.length}</CountBadge>
                    </MobileNavLink>
                  )}
                  {hasActiveSubscriptions && (
                    <MobileNavLink href="#active" data-active={active === "active" ? "" : undefined}>
                      Active Subscriptions <CountBadge>{activeSubscriptions.length}</CountBadge>
                    </MobileNavLink>
                  )}
                  {hasCompletedPurchases && (
                    <MobileNavLink href="#completed" data-active={active === "confirmed" ? "" : undefined}>
                      Completed Purchases <CountBadge>{completedPurchases.length}</CountBadge>
                    </MobileNavLink>
                  )}
                </MobileNav>
              )}
              <Flex
                css={{
                  flex: "1 0 0px",
                  width: "100%",
                  maxW: 960 + 32,
                  mx: "auto",
                  overflow: "hidden",
                }}
              >
                <Box
                  css={{
                    pr: 16,
                    py: 16,
                    borderRight: `1px solid rgba(0, 0, 0, 0.1)`,
                    width: 320,
                    display: "none",
                    "@md": { display: "block" },
                    "@lg": {
                      pt: 0,
                    },
                  }}
                >
                  {hasUpcomingPurchases && (
                    <DesktopNavLink data-active={active === "upcoming" ? "" : undefined} href="#upcoming">
                      Upcoming Bookings & Events <CountBadge>{upcomingPurchases.length}</CountBadge>
                    </DesktopNavLink>
                  )}
                  {hasActiveSubscriptions && (
                    <DesktopNavLink data-active={active === "active" ? "" : undefined} href="#active">
                      Active Subscriptions <CountBadge>{activeSubscriptions.length}</CountBadge>
                    </DesktopNavLink>
                  )}
                  <DesktopNavLink data-active={active === "confirmed" ? "" : undefined} href="#completed">
                    Completed Purchases <CountBadge>{completedPurchases.length}</CountBadge>
                  </DesktopNavLink>
                </Box>
                <Box
                  ref={scrollContainerRef}
                  css={{
                    overflowY: "auto",
                    flex: "1 0 0px",
                    px: 16,
                    pt: 16,
                    "@lg": { pt: 0 },
                  }}
                >
                  <Box css={{ spaceY: 24 }}>
                    {hasUpcomingPurchases && (
                      <Box id="upcoming" css={{ spaceY: 16 }}>
                        {showHeadings && (
                          <Heading
                            as="h2"
                            css={{
                              fontScale: "md",
                              weight: 500,
                              color: colors.navy,
                            }}
                          >
                            Upcoming Booking & Events
                          </Heading>
                        )}

                        {upcomingPurchases.map(purchase => {
                          return <PurchaseCard key={purchase.purchase_id} purchase={purchase} />;
                        })}
                      </Box>
                    )}

                    {hasActiveSubscriptions && (
                      <Box id="active" css={{ spaceY: 16 }}>
                        {showHeadings && (
                          <Heading
                            as="h2"
                            css={{
                              fontScale: "md",
                              weight: 500,
                              color: colors.navy,
                            }}
                          >
                            Active Subscriptions
                          </Heading>
                        )}

                        {activeSubscriptions.map(s => (
                          <SubscriptionCard key={s.subscription_id} subscription={s} />
                        ))}
                      </Box>
                    )}

                    {hasCompletedPurchases && (
                      <Box id="completed" css={{ spaceY: 16 }}>
                        {showHeadings && (
                          <Heading
                            as="h2"
                            css={{
                              fontScale: "md",
                              weight: 500,
                              color: colors.navy,
                            }}
                          >
                            Completed Purchases
                          </Heading>
                        )}

                        {completedPurchases.map(purchase => {
                          return <PurchaseCard key={purchase.purchase_id} purchase={purchase} />;
                        })}
                      </Box>
                    )}
                    <Box>
                      {purchaseHistoryQuery.hasNextPage && (
                        <InfiniteScrollEnd
                          onReach={() => {
                            purchaseHistoryQuery.fetchNextPage();
                          }}
                        >
                          <Spinner />
                        </InfiniteScrollEnd>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Flex>
            </>
          )}
        />

        <PurchaseDialog
          purchaseId={showReceipt}
          open={!!showReceipt}
          justPurchased
          onOpenChange={open => {
            if (!open) {
              navigate({ to: "/purchases", search: { showReceipt: undefined } });
            }
          }}
        />
      </Flex>
    </PageWrapper>
  );
}

const MobileNav = styled("div", {
  minH: 44,
  boxShadow: `inset 0 -2px #F1F0F1`,
  px: 8,
  display: "flex",
  overflowX: "auto",
  "@md": { display: "none" },
});

const MobileNavLink = styled("a", {
  display: "inline-flex",
  items: "center",
  height: 42,
  px: 8,
  flexShrink: 0,
  wordWrap: "nowrap",
  fontScale: "xs",
  color: colors.text2,
  "&[data-active]": {
    color: colors.navy,
    weight: 500,
    boxShadow: "0 2px 0 #000",
  },
});

const DesktopNavLink = styled("a", {
  display: "flex",
  items: "center",
  height: 36,
  px: 16,
  rounded: 8,
  fontScale: "xs",
  color: colors.navy,
  justify: "space-between",
  weight: 500,
  "&[data-active]": {
    backgroundColor: colors.cyanLight,
  },
});

const CountBadge = styled("span", {
  display: "inline-flex",
  items: "center",
  justify: "center",
  height: 16,
  px: 4,
  borderRadius: 9999,
  bg: colors.warmGrey,
  color: "#78767C",
  fontScale: "xs",
  weight: 500,
  ml: 8,
  minW: 16,
  "[data-active] > &": {
    backgroundColor: colors.navy,
    color: "white",
  },
});
